import React from 'react';
import type { WeatherImportLocation } from '../../types/simulation';

// Simplified equirectangular projection for the contiguous US. The SAME projection
// is used for both the rough border outline and the city markers, so the dots
// always land on the silhouette.
const LON_MIN = -125;
const LON_MAX = -66;
const LAT_MIN = 24;
const LAT_MAX = 49.5;
const W = 620;
const H = 360;

const projectX = (lon: number) => ((lon - LON_MIN) / (LON_MAX - LON_MIN)) * W;
const projectY = (lat: number) => ((LAT_MAX - lat) / (LAT_MAX - LAT_MIN)) * H;

// Rough contiguous-US border traced clockwise from the Pacific Northwest. Deliberately
// low-poly / blocky — an "X-COM / UFO Defense" DOS-era look, not a precise map.
const US_BORDER: Array<[number, number]> = [
  [-124.5, 48.4], [-124, 40.5], [-120.5, 34.5], [-117.1, 32.5],
  [-114.8, 32.5], [-111, 31.3], [-108, 31.3], [-106.5, 31.8],
  [-103, 29], [-99, 26.5], [-97.1, 25.9], [-94, 29.7],
  [-90, 29], [-87.5, 30.4], [-84, 30], [-82, 29],
  [-80.1, 25.2], [-80.1, 26.8], [-81.2, 31.1], [-79, 33.8],
  [-76, 37], [-74, 39.5], [-71, 41.3], [-70, 43.2],
  [-67, 44.6], [-69, 47.4], [-71.5, 45], [-76.5, 43.4],
  [-79, 43.3], [-82.5, 41.7], [-83.2, 45.8], [-90, 46.7],
  [-95, 49], [-104, 49], [-123, 48.9],
];

const borderPoints = US_BORDER.map(([lon, lat]) => `${projectX(lon).toFixed(1)},${projectY(lat).toFixed(1)}`).join(' ');

export interface UsLocationMapProps {
  locations: WeatherImportLocation[];
  selectedKey: string | null;
  onSelect: (location: WeatherImportLocation) => void;
}

const COLORS = {
  sea: '#0b1f2a',
  land: '#1f4d3a',
  landStroke: '#3fae7a',
  grid: '#13323f',
  dot: '#7fd8a8',
  dotSelected: '#ffd166',
  label: '#bfe6cf',
  labelSelected: '#ffe9a8',
};

const UsLocationMap: React.FC<UsLocationMapProps> = ({ locations, selectedKey, onSelect }) => (
  <svg
    viewBox={`0 0 ${W} ${H}`}
    width="100%"
    style={{
      display: 'block',
      borderRadius: 6,
      border: `2px solid ${COLORS.landStroke}`,
      background: COLORS.sea,
      imageRendering: 'pixelated',
      fontFamily: 'monospace',
    }}
    role="group"
    aria-label="Карта локаций США"
  >
    {/* retro grid */}
    {Array.from({ length: 13 }, (_, i) => (
      <line key={`v${i}`} x1={(i * W) / 12} y1={0} x2={(i * W) / 12} y2={H} stroke={COLORS.grid} strokeWidth={1} />
    ))}
    {Array.from({ length: 8 }, (_, i) => (
      <line key={`h${i}`} x1={0} y1={(i * H) / 7} x2={W} y2={(i * H) / 7} stroke={COLORS.grid} strokeWidth={1} />
    ))}

    {/* land silhouette */}
    <polygon
      points={borderPoints}
      fill={COLORS.land}
      stroke={COLORS.landStroke}
      strokeWidth={2}
      strokeLinejoin="miter"
    />

    {/* city markers */}
    {locations.map((loc) => {
      const x = projectX(loc.longitude);
      const y = projectY(loc.latitude);
      const selected = loc.key === selectedKey;
      const size = selected ? 12 : 8;
      return (
        <g
          key={loc.key}
          transform={`translate(${x}, ${y})`}
          onClick={() => onSelect(loc)}
          style={{ cursor: 'pointer' }}
          role="button"
          aria-pressed={selected}
          aria-label={loc.name}
        >
          {selected && (
            <rect x={-size} y={-size} width={size * 2} height={size * 2} fill="none" stroke={COLORS.dotSelected} strokeWidth={1.5} opacity={0.7}>
              <animate attributeName="opacity" values="0.2;0.9;0.2" dur="1.4s" repeatCount="indefinite" />
            </rect>
          )}
          <rect
            x={-size / 2}
            y={-size / 2}
            width={size}
            height={size}
            fill={selected ? COLORS.dotSelected : COLORS.dot}
            stroke={COLORS.sea}
            strokeWidth={1}
          />
          <text
            x={size}
            y={4}
            fontSize={selected ? 12 : 10}
            fontWeight={selected ? 700 : 400}
            fill={selected ? COLORS.labelSelected : COLORS.label}
            style={{ pointerEvents: 'none', textShadow: '0 1px 2px #000' }}
          >
            {loc.name}
          </text>
        </g>
      );
    })}
  </svg>
);

export default UsLocationMap;
